import React, { useState, useEffect, Fragment } from 'react';
import { Input, Table, message, Modal, Tag, Button, Space, Tooltip, Form } from "antd";
import { ReloadOutlined, EyeOutlined, DeleteOutlined, PlayCircleOutlined, PlusOutlined, EditOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router';
import useRequest from '@/utils/useRequest';
import useWscTableList from '@/component/UseWscTableList';
import useWscCustomColumns from '@/component/UseWscCustomColumns';
import ColumnSettings from '@/component/ColumnSettings';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import 'highlight.js/styles/github.css';
import hljs from 'highlight.js/lib/core';
import xml from 'highlight.js/lib/languages/xml';
import javascript from 'highlight.js/lib/languages/javascript';
import json from 'highlight.js/lib/languages/json';
import plaintext from 'highlight.js/lib/languages/plaintext';

// 注册语言支持
hljs.registerLanguage('xml', xml);
hljs.registerLanguage('html', xml); // HTML使用XML高亮
hljs.registerLanguage('javascript', javascript);
hljs.registerLanguage('json', json);
hljs.registerLanguage('plaintext', plaintext);
import dayjs from 'dayjs';

const { Search } = Input;

const initParams = {
  pagination: {
    current: 1,
    pageSize: 10
  },
  filters: {},
  sorter: null
};

const BpmnDefinitions = () => {
  const navigate = useNavigate();
  const { request } = useRequest();
  const { list, isLoading, pagination, setPage, setFilter, filters, setSorter, Refresh } = useWscTableList({
    url: "http://127.0.0.1:8080/api/v1/processes/list", 
    initParams: initParams
  });
  const { Refresh: refreshColumns, colsettings } = useWscCustomColumns({ key: "bpmn_definitions" });
  const [columns, setColumns] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [startModalVisible, setStartModalVisible] = useState(false);
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [startForm] = Form.useForm();

  // 默认列配置
  const defaultColumns = [
  
    {
      title: '流程名称',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      visible: true,
      render: (text) => (
        <Tooltip title={text}>
          <span>{text || '-'}</span>
        </Tooltip>
      )
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      visible: true,
      render: (text) => (
        <Tooltip title={text}>
          <span>{text || '-'}</span>
        </Tooltip>
      )
    },
    {
      title: '部署时间',
      dataIndex: 'deployed_at',
      key: 'deployed_at',
      width: 160,
      visible: true,
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '状态',
      dataIndex: 'executable',
      key: 'executable',
      width: 100,
      align: 'center',
      visible: true,
      render: (executable) => {
        return executable ? 
          <Tag color="green">可执行</Tag> : 
          <Tag color="red">不可执行</Tag>;
      }
    }
  ];

  const ExtendColumns = [
    {
      title: <div>操作 <ColumnSettings topic="bpmn_definitions" colsettings={colsettings} list={list} onFinish={refreshColumns} /></div>,
      dataIndex: 'id',
      key: 'id',
      width: 150,
      fixed: 'right',
      render: (id, record) => (
        <Fragment>
          <Button 
            type="link" 
            icon={<EditOutlined />} 
            onClick={() => handleNavigateToExcalidrawEditor(record)}
          >
            可视化编辑
          </Button>
          <Button 
            type="link" 
            icon={<PlayCircleOutlined />} 
            onClick={() => handleStartProcess(record)}
          >
            启动
          </Button>
          <Button 
            type="link" 
            icon={<EyeOutlined />} 
            onClick={() => handleViewDetail(record)}
          >
            详情
          </Button>
          <Button 
            type="link" 
            danger
            icon={<DeleteOutlined />} 
            onClick={() => handleDelete(record)}
          >
            删除
          </Button>
        </Fragment>
      )
    }
  ];

  // 初始化加载
  useEffect(() => {
    // 合并自定义列设置和默认列设置，defaultColumns优先
    let mergedColumns;
    
    if (colsettings && colsettings.length > 0) {
      // 创建一个Map来存储defaultColumns，以dataIndex为键
      const defaultColumnsMap = new Map();
      defaultColumns.forEach(col => {
        defaultColumnsMap.set(col.dataIndex, col);
      });
      
      // 创建一个Map来存储colsettings，以dataIndex为键
      const customColumnsMap = new Map();
      colsettings.forEach(col => {
        customColumnsMap.set(col.dataIndex, col);
      });
      
      // 合并列：按照colsettings的顺序优先，defaultColumns配置优先
      mergedColumns = [];
      
      // 首先按照colsettings的顺序添加列
      colsettings.forEach(customCol => {
        const defaultCol = defaultColumnsMap.get(customCol.dataIndex);
        if (defaultCol) {
          // 如果在defaultColumns中存在，使用defaultColumns的配置但采用colsettings的visible属性
          mergedColumns.push({
            ...defaultCol,
            visible: customCol.visible
          });
        } else {
          // 如果在defaultColumns中不存在，直接使用colsettings的配置
          mergedColumns.push(customCol);
        }
      });
      
      // 然后添加defaultColumns中独有的列（不在colsettings中的）
      defaultColumns.forEach(defaultCol => {
        if (!customColumnsMap.has(defaultCol.dataIndex)) {
          mergedColumns.push(defaultCol);
        }
      });
      
      // 只显示visible为true的列
      mergedColumns = mergedColumns.filter(item => item.visible);
    } else {
      // 如果没有自定义列设置，使用默认列设置
      mergedColumns = defaultColumns.filter(item => item.visible);
    }
    
    let cols = mergedColumns.concat(ExtendColumns);
    setColumns(cols);
  }, [colsettings]);

  // 处理表格变化（分页、排序、筛选）
  const handleTableChange = (pagination, _filters, sorter) => {
    setPage(pagination.current);
    setFilter({ ...filters, ..._filters });
    setSorter(sorter);
  };

  // 处理搜索
  const handleSearch = (value) => {
    setSearchKeyword(value);
    setFilter({ ...filters, keywords: value });
  };

  // 重置搜索
  const handleReset = () => {
    setSearchKeyword('');
    setFilter(initParams.filters);
    setPage(1);
  };

  // 刷新列表
  const handleRefresh = () => {
    Refresh();
  };

  // 跳转到部署页面
  const handleNavigateToDeploy = () => {
    navigate('/bpmn/deploy');
  };

  // 跳转到 Excalidraw 编辑器
  const handleNavigateToExcalidrawEditor = (record) => {
    // 使用流程的 key 而不是 id，因为 API 需要 process_key
    navigate(`/bpmn/excalidraw-editor/${record.key}`);
  };

  // 新建 Excalidraw 流程
  const handleCreateExcalidrawProcess = () => {
    navigate('/bpmn/excalidraw-editor');
  };

  // 启动流程
  const handleStartProcess = (record) => {
    setSelectedProcess(record);
    setStartModalVisible(true);
    startForm.resetFields();
  };

  // 确认启动流程
  const handleConfirmStart = async () => {
    try {
      const values = await startForm.validateFields();
      const { business_key, variables } = values;
      
      // 解析variables JSON字符串
      let parsedVariables = {};
      if (variables) {
        try {
          parsedVariables = JSON.parse(variables);
        } catch (error) {
          message.error('变量格式错误，请输入有效的JSON格式');
          return;
        }
      }

      const result = await request(
        `http://localhost:8080/api/v1/processes/${selectedProcess.key}/start`,
        "POST",
        "json",
        {
          business_key: business_key,
          variables: parsedVariables
        }
      );

      if (result && result.success !== false) {
        message.success('流程启动成功');
        setStartModalVisible(false);
        startForm.resetFields();
      } else {
        message.error(result?.message || '流程启动失败');
      }
    } catch (error) {
      console.error('启动流程失败:', error);
      message.error('启动流程失败');
    }
  };

  // 取消启动
  const handleCancelStart = () => {
    setStartModalVisible(false);
    startForm.resetFields();
  };

  // 查看详情
  const handleViewDetail = async (record) => {
    try {
      // 获取流程定义的XML内容
      const xmlResult = await request(
        `http://127.0.0.1:8080/api/v1/processes/definitions/${record.key}/xml`,
        "GET"
      );
      
      // 处理返回的数据格式：{success: true, data: "xml内容", message: "...", timestamp: "..."}
      let xmlContent = '无法获取XML内容';
      if (xmlResult?.success && xmlResult?.data) {
        // 解码Unicode转义字符
        xmlContent = xmlResult.data.replace(/\\u([0-9a-fA-F]{4})/g, (match, code) => {
          return String.fromCharCode(parseInt(code, 16));
        });
      } else {
        // 兼容旧格式
        xmlContent = xmlResult?.bpmn20Xml || xmlResult?.xml || '无法获取XML内容';
      }
      
      Modal.info({
        title: (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <EyeOutlined style={{ color: '#1890ff' }} />
            <span>流程定义详情</span>
          </div>
        ),
        width: 900,
        content: (
          <div style={{ padding: '8px 0' }}>
            {/* 基本信息卡片 */}
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '20px',
              color: 'white',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                top: '-50px',
                right: '-50px',
                width: '100px',
                height: '100px',
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '50%'
              }} />
              <div style={{
                position: 'absolute',
                bottom: '-30px',
                left: '-30px',
                width: '60px',
                height: '60px',
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '50%'
              }} />
              
              <div style={{ position: 'relative', zIndex: 1 }}>
                <h3 style={{ 
                  margin: '0 0 8px 0', 
                  color: 'white', 
                  fontSize: '20px',
                  fontWeight: '600'
                }}>
                  {record.name || '未命名流程'}
                </h3>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  marginBottom: '12px'
                }}>
                  <Tag color={record.suspended ? 'red' : 'green'} style={{ 
                    borderRadius: '12px',
                    padding: '2px 12px',
                    border: 'none',
                    fontWeight: '500'
                  }}>
                    {record.suspended ? '已挂起' : '运行中'}
                  </Tag>
                  <span style={{ 
                    background: 'rgba(255,255,255,0.2)',
                    padding: '2px 8px',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}>
                    版本 {record.version || '1'}
                  </span>
                </div>
                <div style={{ 
                  fontSize: '13px', 
                  opacity: 0.9,
                  fontFamily: 'Monaco, Consolas, monospace'
                }}>
                  {record.key || 'unknown-key'}
                </div>
              </div>
            </div>

            {/* 详细信息网格 */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '16px',
              marginBottom: '20px'
            }}>
              <div style={{
                background: '#f8f9fa',
                borderRadius: '8px',
                padding: '16px',
                border: '1px solid #e9ecef'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  marginBottom: '8px' 
                }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    background: '#1890ff',
                    borderRadius: '50%',
                    marginRight: '8px'
                  }} />
                  <span style={{ 
                    fontSize: '12px', 
                    color: '#666', 
                    fontWeight: '500',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>定义标识</span>
                </div>
                <div style={{ 
                  fontSize: '14px', 
                  color: '#333',
                  fontFamily: 'Monaco, Consolas, monospace',
                  wordBreak: 'break-all',
                  lineHeight: '1.4'
                }}>
                  {record.id}
                </div>
              </div>

              <div style={{
                background: '#f8f9fa',
                borderRadius: '8px',
                padding: '16px',
                border: '1px solid #e9ecef'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  marginBottom: '8px' 
                }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    background: '#52c41a',
                    borderRadius: '50%',
                    marginRight: '8px'
                  }} />
                  <span style={{ 
                    fontSize: '12px', 
                    color: '#666', 
                    fontWeight: '500',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>分类信息</span>
                </div>
                <div style={{ fontSize: '14px', color: '#333' }}>
                  {record.category || '未分类'}
                </div>
              </div>

              <div style={{
                background: '#f8f9fa',
                borderRadius: '8px',
                padding: '16px',
                border: '1px solid #e9ecef'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  marginBottom: '8px' 
                }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    background: '#fa8c16',
                    borderRadius: '50%',
                    marginRight: '8px'
                  }} />
                  <span style={{ 
                    fontSize: '12px', 
                    color: '#666', 
                    fontWeight: '500',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>资源名称</span>
                </div>
                <div style={{ fontSize: '14px', color: '#333' }}>
                  {record.resourceName || '未指定'}
                </div>
              </div>

              <div style={{
                background: '#f8f9fa',
                borderRadius: '8px',
                padding: '16px',
                border: '1px solid #e9ecef'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  marginBottom: '8px' 
                }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    background: '#722ed1',
                    borderRadius: '50%',
                    marginRight: '8px'
                  }} />
                  <span style={{ 
                    fontSize: '12px', 
                    color: '#666', 
                    fontWeight: '500',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>部署时间</span>
                </div>
                <div style={{ fontSize: '14px', color: '#333' }}>
                  {record.deployed_at ? dayjs(record.deployed_at).format('YYYY-MM-DD HH:mm:ss') : '未部署'}
                </div>
              </div>
            </div>
            <div>
              <h4 style={{ marginBottom: 8, fontWeight: 'bold' }}>BPMN XML定义:</h4>
              <div style={{ 
                border: '1px solid #d9d9d9',
                borderRadius: 6,
                maxHeight: 450,
                overflow: 'auto',
                backgroundColor: '#ffffff'
              }}>
                {/* 语言选择器 */}
                <div style={{
                  padding: '8px 12px',
                  borderBottom: '1px solid #e8e8e8',
                  backgroundColor: '#f5f5f5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <span style={{ color: '#333333', fontSize: '12px', fontWeight: 500 }}>BPMN XML 定义</span>
                  <select 
                    defaultValue="xml"
                    style={{
                      backgroundColor: '#ffffff',
                      color: '#333333',
                      border: '1px solid #d9d9d9',
                      borderRadius: '4px',
                      padding: '2px 8px',
                      fontSize: '12px',
                      outline: 'none'
                    }}
                    onChange={(e) => {
                      const language = e.target.value;
                      const codeElement = e.target.closest('.xml-viewer').querySelector('code');
                      if (codeElement) {
                        codeElement.className = `language-${language}`;
                        hljs.highlightElement(codeElement);
                      }
                    }}
                  >
                    <option value="xml">XML</option>
                    <option value="html">HTML</option>
                    <option value="javascript">JavaScript</option>
                    <option value="json">JSON</option>
                    <option value="plaintext">Plain Text</option>
                  </select>
                </div>
                
                {/* 代码显示区域 */}
                <div className="xml-viewer" style={{ 
                  padding: '16px',
                  backgroundColor: '#ffffff',
                  overflow: 'auto',
                  maxHeight: '380px'
                }}>
                  <pre style={{ 
                    margin: 0, 
                    padding: 0,
                    backgroundColor: 'transparent',
                    border: 'none',
                    fontSize: '13px',
                    lineHeight: '1.5',
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace'
                  }}>
                    <code 
                      className="language-xml"
                      ref={(el) => {
                        if (el && xmlContent) {
                          el.textContent = xmlContent;
                          hljs.highlightElement(el);
                        }
                      }}
                      style={{
                        display: 'block',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all'
                      }}
                    />
                  </pre>
                </div>
              </div>
            </div>
          </div>
        ),
        okText: '关闭'
      });
    } catch (error) {
      console.error('获取流程定义详情失败:', error);
      // 如果获取XML失败，仍然显示基本信息
      Modal.info({
        title: '流程定义详情',
        width: 600,
        content: (
          <div>
            <p><strong>定义ID:</strong> {record.id}</p>
            <p><strong>流程名称:</strong> {record.name || '-'}</p>
            <p><strong>流程Key:</strong> {record.key || '-'}</p>
            <p><strong>版本:</strong> {record.version || '-'}</p>
            <p><strong>状态:</strong> {record.suspended ? '挂起' : '活跃'}</p>
            <p><strong>分类:</strong> {record.category || '-'}</p>
            <p><strong>资源名称:</strong> {record.resourceName || '-'}</p>
            <p><strong>部署时间:</strong> {record.deploymentTime ? dayjs(record.deploymentTime).format('YYYY-MM-DD HH:mm:ss') : '-'}</p>
            <p style={{ color: 'red', marginTop: 16 }}><strong>XML内容:</strong> 获取失败，请检查网络连接或联系管理员</p>
          </div>
        ),
        okText: '关闭'
      });
    }
  };

  // 删除定义
  const handleDelete = (record) => {
    Modal.confirm({
      title: '确认删除',
      content: `您确定要删除流程定义 "${record.name || record.id}" 吗？`,
      okText: '确认',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        try {
          const result = await request(`http://127.0.0.1:8080/api/v1/processes/definitions/${record.id}/delete`, "POST", "json", {});
          if (result && result.success !== false) {
            message.success('流程定义已删除');
            handleRefresh();
          } else {
            message.error(result?.message || '删除失败');
          }
        } catch (error) {
          console.error('删除流程定义失败:', error);
          message.error('删除流程定义失败');
        }
      }
    });
  };

  return (
    <div style={{ maxHeight: 'calc(100vh - 120px)', overflowY: 'scroll' }} className='bg-white pl-4 pb-4'>
      {/* 搜索和操作区域 */}
      <div className="flex flex-row mb-4 items-center justify-between">
        <div className="flex flex-row items-center mt-4">
          <span className="text-base text-gray-800 font-bold mr-4">查询</span>
          <div>
            <Search 
              placeholder="请输入定义名称或Key" 
              onSearch={handleSearch}
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              enterButton 
              style={{ width: 300 }}
            />
          </div>
        </div>
        <div className="flex flex-row items-center mt-4 mr-4">
          <Button onClick={handleReset} className="mr-2">
            重置
          </Button>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleNavigateToDeploy}
            className="mr-2"
          >
            新增部署流程
          </Button>
          <Button 
            type="primary" 
            icon={<EditOutlined />} 
            onClick={handleCreateExcalidrawProcess}
            className="mr-2"
            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
          >
            可视化设计流程
          </Button>
          <Button 
            type="primary" 
            icon={<ReloadOutlined />} 
            onClick={handleRefresh}
            loading={isLoading}
          >
            刷新
          </Button>
        </div>
      </div>

      {/* 表格 */}
      <Table
        rowKey="id"
        loading={isLoading}
        bordered
        dataSource={list}
        columns={columns}
        pagination={pagination}
        onChange={handleTableChange}
        size="small"
        scroll={{ x: 1200 }}
      />

      {/* 启动流程模态框 */}
      <Modal
        title={`启动流程: ${selectedProcess?.name || selectedProcess?.key || ''}`}
        open={startModalVisible}
        onOk={handleConfirmStart}
        onCancel={handleCancelStart}
        okText="启动"
        cancelText="取消"
        width={600}
      >
        <Form
          form={startForm}
          layout="vertical"
          initialValues={{
            business_key: '',
            variables: '{\n  "user_name": "张三",\n  "amount": 1000\n}'
          }}
        >
          <Form.Item
            label="业务键 (Business Key)"
            name="business_key"
            rules={[
              { required: true, message: '请输入业务键' }
            ]}
          >
            <Input 
              placeholder="例如: ORDER-2024-001" 
              style={{ width: '100%' }}
            />
          </Form.Item>
          
          <Form.Item
            label="流程变量 (Variables)"
            name="variables"
            extra='请输入有效的JSON格式，例如: {"user_name": "张三", "amount": 1000}'
          >
            <Input.TextArea 
              rows={6}
              placeholder='{\n  "user_name": "张三",\n  "amount": 1000\n}'
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BpmnDefinitions;